import { PAYSLIP_TEMPLATES } from "@nxsys/utils";

export const downloadFile = (url: string) => {
  if (!url) return;
  const a = document.createElement('a');
  a.href = url;
  a.download = url.split('/').pop() || 'template';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

export const MASTER_TEMPLATE_FILE_PATH = {
  employmentIntermediary:
    '/templates/Employment_Intermediary_Report_Template.csv',
  umbrella: {
    importAgency: '/templates/umbrella/Agency_master_template_V5.2.xlsx',
    importEmployee: '/templates/umbrella/Emp_Import_Master_Template_v5.3.xlsx',
    importProspect: '/templates/umbrella/Prospects_import_master_template_v5.2.xlsx',
    importTimesheet: '/templates/umbrella/Time_sheets_master_template_v5.2.xlsx',
  },
  uk: {
    importEmployee: '/templates/uk/Emp_Import_Master_Template_v3_Bureau.xlsx',
    importProspect: '/templates/uk/Prospects_import_master_template_v5.2_Bureau.xlsx',
    importTimesheet: '/templates/uk/time_sheets_master_template_v4_Bureau.xlsx',
  },
  ireland: {
    importEmployee: '/templates/ireland/Emp_Import_Master_Template_v4_Ireland.xlsx',
    importProspect: '/templates/ireland/Ireland_Prospects_import_master_template_v5.2.xlsx',
    importTimesheet: '/templates/ireland/time_sheets_master_template_v4_Bureau.xlsx',
  },
  // NX-5228: an Irish umbrella tenant used to be handed the UK umbrella
  // templates, whose columns are NI number / Post Code / UTR / tax code.
  // importEmployee carries the backend's Irish umbrella export headers
  // (IrelandSubcontractorHeaders({isBureau: false})), which its import reads
  // with the Irish umbrella columns, minus Working Time Regulation: Ireland has
  // no individual opt-out, so the import does not read it. Prospects follow the backend, which reads
  // every Irish prospect file with the Irish columns whatever the product.
  // Timesheets must be the umbrella file: a non-bureau upload goes to
  // /import/excel/timesheet, which requires Agency ID + Agency Ref, and the
  // bureau-Ireland file (Employer Ref, no Agency ID) failed every row.
  umbrellaIreland: {
    importAgency: '/templates/umbrella/Agency_master_template_V5.2.xlsx',
    importEmployee:
      '/templates/umbrella-ireland/Emp_Import_Master_Template_v1_Umbrella_Ireland.xlsx',
    importProspect: '/templates/ireland/Ireland_Prospects_import_master_template_v5.2.xlsx',
    importTimesheet: '/templates/umbrella/Time_sheets_master_template_v5.2.xlsx',
  },
};

export const getDemoTemplateLayout = (
  templateName: string,
  paymentModel: string,
  isIreland: boolean = false,
) => {
  // Ireland has only one PAYE template
  if (isIreland && (paymentModel === 'PAYEE' || paymentModel === 'PAYE_HYBRID' || paymentModel === 'PAYE_FTH')) {
    return PAYSLIP_PAYE_IRELAND_DEFAULT_DEMO_URL;
  }
  if (paymentModel === 'UMBRELLA') {
    switch (templateName) {
      case PAYSLIP_TEMPLATES.LAYOUT_2.value:
        return UMBRELLA_LAYOUT_2_DEMO_URL;
      case PAYSLIP_TEMPLATES.LAYOUT_3.value:
        return UMBRELLA_LAYOUT_3_DEMO_URL;
      default:
        return PAYSLIP_UMBRELLA_DEFAULT_DEMO_URL;
    }
  }
  // Umbrella Hybrid has its own pair of templates on the backend
  // (hybridPayslip / hybridPayslipNew), so it must not fall back to the PAYE
  // samples — Layout 2 used to preview a PAYE payslip.
  if (paymentModel === 'HYBRID') {
    switch (templateName) {
      case PAYSLIP_TEMPLATES.LAYOUT_2.value:
        return PAYSLIP_HYBRID_LAYOUT_2_DEMO_URL;
      default:
        return PAYSLIP_HYBRID_DEFAULT_DEMO_LAYOUT;
    }
  }
  // PAYE Hybrid deliberately has no branch of its own: the backend picks its
  // template from the same switch as PAYE (utils.ts getPayeUmbrellaPayslipTemplate
  // handles PAYEE, PAYE_HYBRID and PAYE_FTH together), so it falls through to
  // the PAYE mapping below. The branch that used to be here previewed the
  // Umbrella Hybrid payslip for Nxsys Default and for Layout 5.
  if (paymentModel === 'PAYE_FTH') {
    switch (templateName) {
      case PAYSLIP_TEMPLATES.PAYE_ETH_LAYOUT_2.value:
        return PAYSLIP_LAYOUT_FTH_2_DEMO_URL;
      case PAYSLIP_TEMPLATES.PAYE_FTH_LAYOUT_3.value:
        return PAYSLIP_LAYOUT_FTH_3_DEMO_URL;
      case PAYSLIP_TEMPLATES.LAYOUT_4.value:
        return PAYSLIP_LAYOUT_4_DEMO_URL;
      default:
        return PAYSLIP_PAYE_DEFAULT_DEMO_URL;
    }
  }
  // PAYEE and other models
  switch (templateName) {
    case PAYSLIP_TEMPLATES.LAYOUT_1.value:
      return PAYSLIP_LAYOUT_1_DEMO_URL;
    case PAYSLIP_TEMPLATES.LAYOUT_2.value:
      return PAYSLIP_LAYOUT_2_DEMO_URL;
    case PAYSLIP_TEMPLATES.LAYOUT_3.value:
      return PAYSLIP_LAYOUT_3_DEMO_URL;
    case PAYSLIP_TEMPLATES.LAYOUT_5.value:
      return PAYSLIP_LAYOUT_5_DEMO_URL;
    case PAYSLIP_TEMPLATES.PAYE_ETH_LAYOUT_2.value:
      return PAYSLIP_LAYOUT_FTH_2_DEMO_URL;
    case PAYSLIP_TEMPLATES.LAYOUT_4.value:
      return PAYSLIP_LAYOUT_4_DEMO_URL;
    case PAYSLIP_TEMPLATES.PAYE_FTH_LAYOUT_3.value:
      return PAYSLIP_LAYOUT_FTH_3_DEMO_URL;
    case PAYSLIP_TEMPLATES.LAYOUT_6.value:
      return PAYSLIP_LAYOUT_6_DEMO_URL;
    default:
      return PAYSLIP_PAYE_DEFAULT_DEMO_URL;
  }
};

export const PAYSLIP_LAYOUT_1_DEMO_URL =
  '/templates/payslip-previews/paye-layout-1.pdf';

export const PAYSLIP_LAYOUT_2_DEMO_URL =
  '/templates/payslip-previews/paye-layout-2.pdf';

export const PAYSLIP_LAYOUT_3_DEMO_URL =
  '/templates/payslip-previews/paye-layout-3.pdf';

export const PAYSLIP_LAYOUT_5_DEMO_URL =
  '/templates/payslip-previews/paye-layout-5.pdf';

export const PAYSLIP_LAYOUT_6_DEMO_URL =
  '/templates/payslip-previews/paye-layout-6.pdf';

export const PAYSLIP_PAYE_DEFAULT_DEMO_URL =
  '/templates/payslip-previews/paye-nxsys-default.pdf';

export const PAYSLIP_UMBRELLA_DEFAULT_DEMO_URL =
  '/templates/payslip-previews/umbrella-nxsys-default.pdf';

export const PAYSLIP_HYBRID_DEFAULT_DEMO_LAYOUT =
  '/templates/payslip-previews/hybrid-nxsys-default.pdf';

export const PAYSLIP_HYBRID_LAYOUT_2_DEMO_URL =
  '/templates/payslip-previews/hybrid-layout-2.pdf';

export const SELF_CIS_DEFAULT_DEMO_LAYOUT =
  '/templates/payslip-previews/cis-nxsys-default.pdf';
export const SELF_CIS_DEFAULT_DEMO_LAYOUT_1 =
  '/templates/payslip-previews/cis-layout-1.pdf';

export const SELF_CIS_DEFAULT_DEMO_LAYOUT_2 =
  '/templates/payslip-previews/cis-layout-2.pdf';
export const SELF_CIS_DEFAULT_DEMO_LAYOUT_3 =
  '/templates/payslip-previews/cis-layout-3.pdf';

export const PAYSLIP_LAYOUT_4_DEMO_URL =
  '/templates/payslip-previews/paye-layout-4.pdf';

export const PAYSLIP_LAYOUT_FTH_2_DEMO_URL =
  '/templates/payslip-previews/paye-fth-layout-2.pdf';

export const PAYSLIP_LAYOUT_FTH_3_DEMO_URL =
  '/templates/payslip-previews/paye-fth-layout-3.pdf';

export const UMBRELLA_LAYOUT_2_DEMO_URL =
  '/templates/payslip-previews/umbrella-layout-2.pdf';
export const UMBRELLA_LAYOUT_3_DEMO_URL =
  '/templates/payslip-previews/umbrella-layout-3.pdf';

export const CIS_LAYOUT_4_DEMO_URL =
  '/templates/payslip-previews/cis-layout-4.pdf';


export const getDemoCisTemplateLayout = (
  templateName: string,
  defaultURL: string = SELF_CIS_DEFAULT_DEMO_LAYOUT,
) => {
  switch (templateName) {
    case PAYSLIP_TEMPLATES.DEFAULT.value:
      return SELF_CIS_DEFAULT_DEMO_LAYOUT;
    case PAYSLIP_TEMPLATES.LAYOUT_1.value:
      return SELF_CIS_DEFAULT_DEMO_LAYOUT_1;
    case PAYSLIP_TEMPLATES.LAYOUT_2.value:
      return SELF_CIS_DEFAULT_DEMO_LAYOUT_2;
    case PAYSLIP_TEMPLATES.CIS_LAYOUT_3.value:
      return SELF_CIS_DEFAULT_DEMO_LAYOUT_3;
    case PAYSLIP_TEMPLATES.CIS_LAYOUT_4.value:
      return CIS_LAYOUT_4_DEMO_URL;
    default:
      return defaultURL;
  }
};

export const RCT_DEFAULT_DEMO_LAYOUT = '/templates/payslip-previews/ireland-rct-default.pdf'

export const INVOICE_DEMO_TEMPLATE = {
  DEFAULT: "https://cis-dev-storage.s3.eu-west-2.amazonaws.com/uploads/7fb1ac20-3fed-456c-aa93-46f534b41317-COM-16/pdf/29-03-2026_15-12-11-59ec1a28-7caf-404a-b08b-e76055ee1148-invoice_INV-84250_INV-1006_COM-16.pdf",
  BREAKDOWN_PER_EMPLOYEE: "https://cis-dev-storage.s3.eu-west-2.amazonaws.com/uploads/be4bf451-4628-4f4b-8fc7-2069e203c632-COM-244/pdf/18-11-2025_07-44-15-a7b8b22c-7488-4e1d-9bc1-b0ad46f425b5-invoice_INV-83907_INV-17_COM-244.pdf",
};

export const getInvoiceDemoTemplate = (
  templateName: string,
) => {
  switch (templateName) {
    case "DEFAULT":
      return INVOICE_DEMO_TEMPLATE.DEFAULT;
    case "BREAKDOWN_PER_EMPLOYEE":
      return INVOICE_DEMO_TEMPLATE.BREAKDOWN_PER_EMPLOYEE;
    default:
      return INVOICE_DEMO_TEMPLATE.DEFAULT;
  }
};

export const PAYSLIP_PAYE_IRELAND_DEFAULT_DEMO_URL =
  '/templates/payslip-previews/ireland-paye-default.pdf';
